/**
 * Decision 0145: what a turn's context was made of, next to what the provider billed. Pure.
 *
 * - `historySize`: the replayed chat history of a cloud turn by kind (person, assistant, tool, wake, prompt, digest,
 *   compaction, file, other), in UTF-8 bytes, measured on the messages the model gets (after the body's filters).
 *   Everything before the newest compaction block is counted apart (`dropped`): Anthropic drops it.
 * - `contextUsage`: the run's usage rows summed (uncached input, cache reads and writes, output, model calls).
 * - `contextShares`: the numbers the run view and Analytics Engine show: per model call, how much is the pack, the
 *   history, the system prompt and tool definitions, and the rest.
 *
 * Token counts are estimates (about 4 bytes a token, as `packSizes`); the provider's usage is the truth for money.
 */
import type {
  ContextPartKind,
  ContextSize,
  ContextUsage,
  HistorySection,
  HistorySize,
  Run,
  Usage,
} from "@minga/protocol"
import { CONTEXT_PART_KINDS } from "@minga/protocol"
import { usageRowV2 } from "./usage"

/** About 4 bytes a token (the same estimate as `packSizes`). */
export const tokensOfBytes = (bytes: number): number => Math.ceil(bytes / 4)

/** UTF-8 length of a string without encoding it (histories are megabytes). */
export const utf8Length = (s: string): number => {
  let n = 0
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i)
    if (c < 0x80) n += 1
    else if (c < 0x800) n += 2
    else if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
      n += 4
      i += 1
    } else n += 3
  }
  return n
}

const jsonBytes = (v: unknown): number => {
  if (v === undefined) return 0
  try {
    return utf8Length(typeof v === "string" ? v : (JSON.stringify(v) ?? ""))
  } catch {
    return 0
  }
}

/** A chat message as the model gets it (an AI SDK `UIMessage`, structurally). */
export interface HistoryMessage {
  readonly id: string
  readonly role: string
  readonly parts: ReadonlyArray<unknown>
}

/** The stored compaction part (cloud `COMPACTION_PART`) and its model form (a text part marked as compaction). */
const COMPACTION_DATA = "data-compaction"
const isCompaction = (p: { type?: unknown; providerMetadata?: unknown }): boolean =>
  p.type === COMPACTION_DATA ||
  (p.type === "text" &&
    (p.providerMetadata as { anthropic?: { type?: unknown } } | undefined)?.anthropic?.type === "compaction")

/** One part's kind and bytes. `textKind` is what a text part of this message is (person, assistant, wake, …). */
const partSize = (raw: unknown, textKind: ContextPartKind): { kind: ContextPartKind; bytes: number } => {
  const p = (raw ?? {}) as Record<string, unknown>
  const type = typeof p.type === "string" ? p.type : ""
  if (isCompaction(p)) {
    const summary = type === COMPACTION_DATA ? (p.data as { summary?: unknown } | undefined)?.summary : p.text
    return { kind: "compaction", bytes: typeof summary === "string" ? utf8Length(summary) : 0 }
  }
  if (type === "text") return { kind: textKind, bytes: typeof p.text === "string" ? utf8Length(p.text) : 0 }
  if (type === "dynamic-tool" || type.startsWith("tool-"))
    return {
      kind: "tool",
      bytes:
        jsonBytes(p.input) +
        jsonBytes(p.output) +
        (typeof p.errorText === "string" ? utf8Length(p.errorText) : 0),
    }
  if (type === "file")
    return {
      kind: "file",
      bytes:
        (typeof p.url === "string" ? utf8Length(p.url) : 0) +
        (typeof p.filename === "string" ? utf8Length(p.filename) : 0),
    }
  if (type === "step-start" || type === "reasoning") return { kind: "other", bytes: 0 }
  return { kind: "other", bytes: jsonBytes(p) }
}

export interface HistorySizeInput {
  /** The messages the model gets, in order (the body's transcript after its filters). */
  readonly messages: ReadonlyArray<HistoryMessage>
  /** Messages whose text is not a person's: earlier turn prompts (`wake`), this turn's (`prompt`), the `digest`. */
  readonly kindOf: (id: string) => ContextPartKind | null
  /** The system prompt (AGENTS.md, a recovery line). */
  readonly system: string
  /** The tool definitions as sent (names, descriptions, input schemas). */
  readonly tools: ReadonlyArray<unknown>
}

/**
 * Decision 0145: a cloud turn's replayed history by kind. Only user and assistant messages count (the body's
 * `modelMessages` drops system lines and reasoning). Everything before the newest compaction block is `dropped`: the
 * request carries it, the provider does not read it.
 */
export const historySize = (input: HistorySizeInput): HistorySize => {
  const msgs = input.messages.filter((m) => m.role === "user" || m.role === "assistant")
  // the newest compaction block: message index and part index
  let cutMsg = -1
  let cutPart = -1
  for (let i = msgs.length - 1; i >= 0 && cutMsg < 0; i--) {
    const parts = msgs[i]?.parts ?? []
    for (let j = parts.length - 1; j >= 0; j--)
      if (isCompaction((parts[j] ?? {}) as { type?: unknown; providerMetadata?: unknown })) {
        cutMsg = i
        cutPart = j
        break
      }
  }
  const sums = new Map<ContextPartKind, { bytes: number; parts: number }>()
  let read = 0
  let dropped = 0
  let droppedBytes = 0
  msgs.forEach((m, i) => {
    const textKind: ContextPartKind = input.kindOf(m.id) ?? (m.role === "assistant" ? "assistant" : "person")
    let messageRead = false
    m.parts.forEach((raw, j) => {
      const s = partSize(raw, textKind)
      if (i < cutMsg || (i === cutMsg && j < cutPart)) {
        droppedBytes += s.bytes
        return
      }
      messageRead = true
      if (s.bytes === 0) return
      const t = sums.get(s.kind) ?? { bytes: 0, parts: 0 }
      t.bytes += s.bytes
      t.parts += 1
      sums.set(s.kind, t)
    })
    if (messageRead) read += 1
    else dropped += 1
  })
  const sections: Array<HistorySection> = CONTEXT_PART_KINDS.flatMap((kind) => {
    const t = sums.get(kind)
    return t ? [{ kind, bytes: t.bytes, parts: t.parts }] : []
  })
  const bytes = sections.reduce((n, s) => n + s.bytes, 0)
  return {
    messages: read,
    bytes,
    tokens: tokensOfBytes(bytes),
    sections,
    droppedMessages: dropped,
    droppedBytes,
    systemBytes: utf8Length(input.system),
    toolBytes: jsonBytes(input.tools),
    tools: input.tools.length,
  }
}

/** Decision 0145: a run's usage rows summed (null: no rows). Uncached input as `usageRowV2` reads every row. */
export const contextUsage = (usage: ReadonlyArray<Usage>): ContextUsage | null => {
  if (usage.length === 0) return null
  let uncached = 0
  let cached = 0
  let written = 0
  let output = 0
  let steps = 0
  for (const raw of usage) {
    const u = usageRowV2(raw)
    uncached += u.inputTokens
    cached += u.cachedInputTokens
    written += u.cacheWriteTokens
    output += u.outputTokens
    steps += u.turns
  }
  const context = uncached + cached + written
  const calls = usage.find((u) => u.calls !== undefined)?.calls
  return {
    steps,
    contextTokens: context,
    uncachedInputTokens: uncached,
    cachedInputTokens: cached,
    cacheWriteTokens: written,
    outputTokens: output,
    perStepTokens: steps > 0 ? Math.round(context / steps) : context,
    ...(calls ? { calls } : {}),
  }
}

/** A CLI session's identity (Claude session id, Codex thread id, …), or null. */
export const sessionKeyOf = (ref: Run["sessionOut"] | undefined): string | null => {
  if (!ref) return null
  const r = ref as unknown as { readonly _tag: string } & Record<string, unknown>
  const id = r.sessionId ?? r.threadId ?? r.conversationId ?? r.id
  return typeof id === "string" ? `${r._tag}:${id}` : null
}

/**
 * Decision 0145: whether a runner run continued a CLI session. `RunStarted.sessionIn` is null for every runner turn
 * (the body, not the job, carries a turn's session), so a turn also counts as resumed when it ended in the same session
 * as the agent's previous run of its kind.
 */
export const resumedSession = (
  run: Pick<Run, "sessionIn" | "sessionOut">,
  previous: Pick<Run, "sessionOut"> | null | undefined,
): boolean => {
  if (run.sessionIn != null) return true
  const out = sessionKeyOf(run.sessionOut)
  return out !== null && out === sessionKeyOf(previous?.sessionOut)
}

/**
 * Decision 0145: a pack's size with the run's mind, session, history and usage added (what the run view's "Context
 * size" and the `run_context` points read). `previous`: the agent's previous run of the same kind that has a session.
 */
export const runContextSize = (
  pack: ContextSize,
  run: Pick<Run, "executor" | "usage" | "sessionIn" | "sessionOut"> | null | undefined,
  history: HistorySize | null,
  previous?: Pick<Run, "sessionOut"> | null,
): ContextSize => {
  const mind =
    run?.executor?._tag === "platform" ? "cloud" : run?.executor?._tag === "runner" ? "runner" : undefined
  const usage = run ? contextUsage(run.usage) : null
  return {
    hash: pack.hash,
    bytes: pack.bytes,
    tokens: pack.tokens,
    files: pack.files,
    ...(mind ? { mind } : {}),
    ...(mind === "runner" && run ? { resumed: resumedSession(run, previous) } : {}),
    ...(history ? { history } : {}),
    ...(usage ? { usage } : {}),
  }
}

/** The pack files every model call of a turn reads (AGENTS.md as the system prompt, the turn file as the prompt). */
const IN_CONTEXT = new Set(["AGENTS.md", ".memory/turn.md"])

/**
 * Decision 0145: one model call's context, split (estimated tokens). For a cloud turn: the system prompt and tool
 * definitions, the history without this turn's prompt, this turn's prompt (the turn file), and what the turn itself
 * added on average (`growth`: the provider's per-call average minus the first request). For a runner turn: the pack
 * files in context and the `rest` (the CLI's own system prompt and tools, a resumed session, the turn's own calls).
 */
export interface ContextShares {
  readonly mind: "cloud" | "runner" | null
  /** AGENTS.md + the turn file. */
  readonly packTokens: number
  readonly turnFileTokens: number
  readonly systemTokens: number
  readonly toolDefTokens: number
  /** The replayed history without this turn's prompt. */
  readonly historyTokens: number
  /** Cloud: system + tools + history (with this turn's prompt): the first request, estimated. */
  readonly firstRequestTokens: number | null
  readonly perStepTokens: number | null
  /** Cloud: per call minus the first request (≥ 0). Runner: per call minus the pack files. */
  readonly restTokens: number | null
  /** The provider's first and last main-loop call (`Usage.calls`; null: not reported). */
  readonly firstCallTokens: number | null
  readonly lastCallTokens: number | null
  /**
   * Runner, from the first call: what is not the pack (the CLI's own prompt and tools, and a resumed session's earlier
   * turns). Cloud: the first call minus the estimated first request (the estimate's error). Null: no `calls`.
   */
  readonly beforeTurnTokens: number | null
}

export const contextShares = (size: ContextSize): ContextShares => {
  const fileBytes = (path: string) => size.files.find((f) => f.path === path)?.bytes ?? 0
  const packBytes = size.files.filter((f) => IN_CONTEXT.has(f.path)).reduce((n, f) => n + f.bytes, 0)
  const h = size.history
  const prompt = h?.sections.find((s) => s.kind === "prompt")?.bytes ?? 0
  const perStep = size.usage && size.usage.steps > 0 ? size.usage.perStepTokens : null
  const mind = size.mind ?? (h ? "cloud" : null)
  const first = h ? tokensOfBytes(h.systemBytes) + tokensOfBytes(h.toolBytes) + h.tokens : null
  const rest =
    perStep === null
      ? null
      : mind === "cloud"
        ? first !== null
          ? Math.max(0, perStep - first)
          : null
        : Math.max(0, perStep - tokensOfBytes(packBytes))
  const calls = size.usage?.calls
  const firstCall = calls ? calls.first : null
  return {
    mind,
    firstCallTokens: firstCall,
    lastCallTokens: calls ? calls.last : null,
    beforeTurnTokens:
      firstCall === null
        ? null
        : mind === "cloud"
          ? first !== null
            ? Math.max(0, firstCall - first)
            : null
          : Math.max(0, firstCall - tokensOfBytes(packBytes)),
    packTokens: tokensOfBytes(packBytes),
    turnFileTokens: tokensOfBytes(fileBytes(".memory/turn.md")),
    systemTokens: h ? tokensOfBytes(h.systemBytes) : tokensOfBytes(fileBytes("AGENTS.md")),
    toolDefTokens: h ? tokensOfBytes(h.toolBytes) : 0,
    historyTokens: h ? tokensOfBytes(Math.max(0, h.bytes - prompt)) : 0,
    firstRequestTokens: first,
    perStepTokens: perStep,
    restTokens: rest,
  }
}
